import React from 'react';
import { formatTemplate } from './formatTemplate';

interface NavigateButtonProps {
  url: string;
  onNavigate: (url: string) => void;
  // Localized "Continue to {0}" template for the button label
  continueTemplate?: string;
  // Localized "opens in new tab" hint appended (visually hidden) to external links
  opensNewTabLabel?: string;
}

/**
 * NavigateButton component
 * Renders a navigation button for same-origin URLs
 */
export const NavigateButton: React.FC<NavigateButtonProps> = ({
  url,
  onNavigate,
  continueTemplate,
  opensNewTabLabel,
}) => {
  // Validate same-origin
  const isSameOrigin = React.useMemo(() => {
    try {
      const targetUrl = new URL(url, window.location.origin);
      return targetUrl.origin === window.location.origin;
    } catch {
      // If URL parsing fails, check if it's a relative path
      return url.startsWith('/');
    }
  }, [url]);

  const handleClick = () => {
    onNavigate(url);
  };

  // Extract a human-meaningful target name from the URL for the label:
  // last path segment when present, else the hostname (external links),
  // else the literal 'page'.
  const getPageName = () => {
    try {
      const urlObj = new URL(url, window.location.origin);
      const pathParts = urlObj.pathname.split('/').filter(Boolean);
      return pathParts[pathParts.length - 1] || urlObj.hostname || 'page';
    } catch {
      return 'page';
    }
  };

  const label = formatTemplate(continueTemplate || 'Continue to {0}', getPageName());

  // Cross-origin URLs open in a new tab
  if (!isSameOrigin) {
    return (
      <div className="omq-chatbot-navigate-container">
        <a
          className="omq-chatbot-navigate-btn"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="omq-chatbot-navigate-icon" aria-hidden="true">↗</span>
          <span className="omq-chatbot-navigate-text">{label}</span>
          <span className="omq-sr-only"> ({opensNewTabLabel || 'opens in new tab'})</span>
        </a>
      </div>
    );
  }

  return (
    <div className="omq-chatbot-navigate-container">
      <button
        className="omq-chatbot-navigate-btn"
        onClick={handleClick}
        type="button"
      >
        <span className="omq-chatbot-navigate-icon" aria-hidden="true">→</span>
        <span className="omq-chatbot-navigate-text">{label}</span>
      </button>
    </div>
  );
};
